/**
 * Amounts and accounts on the NEAR pages (blackbeard/observer#44).
 *
 * Amounts arrive in base units with the token they belong to, and the token's
 * decimals arrive separately — so a token whose metadata could not be read is
 * still shown, in base units and named by its contract, rather than with a
 * guessed decimal count.
 */

import type { NearToken } from '../api/generated/NearToken'
import { exactTokens, tokens as fmtTokens } from './format'
import { contractOf, tokenLabel } from './tokens'

/** yoctoNEAR per NEAR. */
export const NEAR_DECIMALS = 24

/** A signed base-unit amount in its token's units: `−1.25 QTC`. */
export function amountText(raw: string, token: string, known: NearToken[]): string {
  const negative = raw.startsWith('-')
  const digits = negative ? raw.slice(1) : raw
  const meta = known.find((t) => t.token === token)
  const sign = negative ? '−' : ''
  if (!meta) return `${sign}${digits} ${contractOf(token)} (base units)`
  return `${sign}${fmtTokens(digits, meta.decimals)} ${tokenLabel(token, meta.symbol)}`
}

/** The exact figure and the contract, for a `title`. */
export function amountTitle(raw: string, token: string, known: NearToken[]): string {
  const digits = raw.replace(/^-/, '')
  const meta = known.find((t) => t.token === token)
  const exact = meta ? exactTokens(digits, meta.decimals) : digits
  return `${raw.startsWith('-') ? '−' : ''}${exact} — ${token}`
}

/**
 * NEAR attached to a call, from yoctoNEAR. A deposit under a millionth of a
 * NEAR is shown in yoctoNEAR: `ft_transfer` requires exactly one attached, as
 * proof the caller signed with a full-access key, and "0 NEAR" would hide it.
 */
export function nearText(yocto: string): string {
  if (yocto.replace(/^0+/, '').length <= NEAR_DECIMALS - 6) return `${yocto} yoctoNEAR`
  return `${fmtTokens(yocto, NEAR_DECIMALS, 6)} NEAR`
}

/**
 * An account id short enough for a cell. Named accounts are kept whole up to a
 * point — `solver-ref-2.near` is the identity and reads as one — while an
 * implicit 64-hex id keeps both ends, which is how a reader compares two.
 */
export function shortNear(account: string): string {
  return account.length > 24 ? `${account.slice(0, 8)}…${account.slice(-6)}` : account
}

/** What NEAR's own rules make an account id, in words. */
export function accountKindText(kind: string): string {
  switch (kind) {
    case 'implicit_ed25519':
      return 'implicit — the id is an ed25519 public key'
    case 'implicit_eth':
      return 'implicit — the id is an Ethereum address'
    default:
      return 'named — registered under this name'
  }
}
